import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError, netErrorStore } from "./api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const STORE_CACHE = "jatak_store";
const cacheStore = (s) => (s ? localStorage.setItem(STORE_CACHE, JSON.stringify(s)) : localStorage.removeItem(STORE_CACHE));
const cachedStore = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_CACHE)) || {};
  } catch {
    return {};
  }
};

const LAST_STORE = "jatak_last_store";
const SWITCH_SAFE = /^\/(overblik|offers|pickup|customers|stats|settings|billing|butikker)\/?$/;

async function switchTo(id) {
  const { data } = await api.post("/account/switch", { store_id: Number(id) });
  localStorage.setItem("jatak_token", data.token);
  localStorage.setItem(LAST_STORE, String(data.store.id));
  return data.store;
}

export const canSimulate = (store) => !!(store && (store.is_owner || store.impersonated));

export function AuthProvider({ children }) {
  const [store, setStoreState] = useState(undefined); // undefined=loading, null=guest, object=store
  const [epoch, setEpoch] = useState(0); // bumps when back online so pages refetch
  const setStore = useCallback((s) => {
    cacheStore(s);
    setStoreState(s);
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("jatak_token");
    if (!token) {
      setStore(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me", { netGuard: false });
      const want = new URLSearchParams(window.location.search).get("butik");
      if (want && Number(want) !== data.store.id && !data.store.staff) {
        try {
          setStore(await switchTo(want));
        } catch {
          setStore(data.store);
        }
        const u = new URL(window.location.href);
        u.searchParams.delete("butik");
        window.history.replaceState(null, "", u.pathname + u.search + u.hash);
        return;
      }
      if (!data.store.staff && !data.store.is_owner && !data.store.impersonated) localStorage.setItem(LAST_STORE, String(data.store.id));
      setStore(data.store);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("jatak_token");
        setStore(null);
      } else {
        // Offline / network / 5xx: keep the session and show last-known store
        setStoreState(cachedStore());
      }
    }
  }, [setStore]);

  const retry = useCallback(() => {
    netErrorStore.clear();
    setEpoch((e) => e + 1);
  }, []);

  useEffect(() => {
    loadMe();
    const onOnline = () => loadMe().then(retry);
    window.addEventListener("online", onOnline);
    window.addEventListener("jatak-locked", loadMe);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("jatak-locked", loadMe);
    };
  }, [loadMe, retry]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("jatak_token", data.token);
    const last = localStorage.getItem(LAST_STORE);
    if (last && Number(last) !== data.store.id && !data.store.is_owner) {
      try {
        setStore(await switchTo(last));
        return;
      } catch {
        localStorage.setItem("jatak_token", data.token);
      }
    }
    setStore(data.store);
  };

  const switchStore = async (id) => {
    await switchTo(id);
    const path = window.location.pathname;
    window.location.assign(SWITCH_SAFE.test(path) ? path : "/overblik");
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem("jatak_token", data.token);
    setStore(data.store);
  };

  const logout = () => {
    localStorage.removeItem("jatak_token");
    localStorage.removeItem(LAST_STORE);
    setStore(null);
  };

  const setToken = (t) => localStorage.setItem("jatak_token", t);

  const staffSignIn = (data) => {
    localStorage.setItem("jatak_token", data.token);
    setStore(data.store);
  };

  const impersonate = (token, impStore) => {
    const admin = localStorage.getItem("jatak_token");
    if (admin) localStorage.setItem("jatak_admin_token", admin);
    localStorage.setItem("jatak_token", token);
    setStore(impStore);
  };

  const stopImpersonate = async () => {
    const admin = localStorage.getItem("jatak_admin_token");
    if (admin) {
      localStorage.setItem("jatak_token", admin);
      localStorage.removeItem("jatak_admin_token");
    }
    await loadMe();
  };

  return (
    <AuthCtx.Provider value={{ store, epoch, retry, login, signup, logout, refresh: loadMe, setToken, switchStore, staffSignIn, impersonate, stopImpersonate }}>
      {children}
    </AuthCtx.Provider>
  );
}

export { apiError };
